import React, { useState } from "react";
import { Calendar, Clock, User, MapPin, RefreshCw, Database, Edit, Plus, Trash2, AlertCircle, FileCheck, History, Filter, Search } from "lucide-react";
import { Card, CardContent, CardDescription, CardTitle, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AppLayout from "@/components/layout/AppLayout";
import { format, isValid, parseISO } from "date-fns";
import ScheduleLogsFilters from "./components/ScheduleLogsFilters";
import { useOptimizedScheduleLogs } from "./hooks/useOptimizedScheduleLogs";
import { useScheduleLogsStorage } from "./hooks/useScheduleLogsStorage";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ScheduleLogs = () => {
  const [filterType, setFilterType] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<string>("all");
  const [selectedGuard, setSelectedGuard] = useState<string>("all");
  const [selectedAdmin, setSelectedAdmin] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const { clearCache } = useScheduleLogsStorage();

  const { 
    changes: filteredChanges, 
    isLoading, 
    lastSync, 
    totalCached,
    hasNewData 
  } = useOptimizedScheduleLogs({
    filterType,
    searchTerm,
    selectedTeam,
    selectedGuard,
    selectedAdmin,
    selectedDate
  });

  const handleClearCache = () => {
    clearCache();
    toast.success("Local cache cleared");
    // Force a page refresh to reload data
    window.location.reload();
  };

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'INSERT': return 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-400';
      case 'DELETE': return 'bg-red-500/10 text-red-600 border border-red-500/20 dark:bg-red-500/20 dark:text-red-400';
      case 'UPDATE': return 'bg-blue-500/10 text-blue-600 border border-blue-500/20 dark:bg-blue-500/20 dark:text-blue-400';
      default: return 'bg-muted/50 text-muted-foreground border border-border';
    }
  };

  const getChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'INSERT': return <Plus className="w-3 h-3" />;
      case 'DELETE': return <Trash2 className="w-3 h-3" />;
      case 'UPDATE': return <Edit className="w-3 h-3" />;
      default: return <AlertCircle className="w-3 h-3" />;
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'INSERT': return 'Added';
      case 'DELETE': return 'Removed';
      case 'UPDATE': return 'Updated';
      default: return type;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'MMM dd, yyyy HH:mm');
      }
    } catch {
      // Fallback for invalid dates
    }
    return dateString;
  };

  const formatDuration = (hours: number | null) => {
    if (!hours) return '';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `(${wholeHours}h ${minutes}m)`;
  };

  const formatDateChange = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'dd/MM/yyyy HH:mm');
      }
    } catch {
      // Fallback for invalid dates
    }
    return dateString;
  };

  const formatFieldChange = (fieldName: string | null, oldValue: string | null, newValue: string | null) => {
    if (!fieldName) return null;
    
    const fieldDisplayNames: { [key: string]: string } = {
      'title': 'Title',
      'description': 'Description',
      'start_time': 'Start Time',
      'end_time': 'End Time',
      'location': 'Location',
      'assigned_guards': 'Assigned Guards'
    };

    const displayName = fieldDisplayNames[fieldName] || fieldName;
    
    // For date/time fields, format them properly
    let displayOldValue = oldValue;
    let displayNewValue = newValue;
    
    if (fieldName === 'start_time' || fieldName === 'end_time') {
      displayOldValue = formatDateChange(oldValue);
      displayNewValue = formatDateChange(newValue);
    }
    
    return (
      <div className="bg-amber-500/10 p-4 rounded-lg border border-amber-500/20 shadow-sm dark:bg-amber-500/20">
        <div className="flex items-center gap-2 mb-3">
          <Edit className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
            {displayName} Change
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">From</span>
            <div className="p-2 bg-red-500/10 rounded border border-red-500/20 text-red-700 dark:bg-red-500/20 dark:text-red-300 font-medium">
              {displayOldValue || 'N/A'}
            </div>
          </div>
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">To</span>
            <div className="p-2 bg-green-500/10 rounded border border-green-500/20 text-green-700 dark:bg-green-500/20 dark:text-green-300 font-medium">
              {displayNewValue || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto p-6 space-y-8">
          {/* Header Section */}
          <Card className="bg-card border-border">
            <CardContent className="p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-primary">
                      <FileCheck className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold text-primary">
                        Schedule Audit Logs
                      </h1>
                      <p className="text-lg text-muted-foreground mt-2">
                        Comprehensive tracking of all schedule changes with complete audit trail
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Stats and Controls */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                  <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl border border-border">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary">
                        <Database className="w-4 h-4 text-primary-foreground" />
                      </div>
                      <div className="text-sm">
                        <div className="font-semibold text-foreground">{totalCached} logs cached</div>
                        {lastSync && (
                          <div className="text-xs text-muted-foreground">
                            Last sync: {format(new Date(lastSync), 'HH:mm')}
                          </div>
                        )}
                      </div>
                    </div>
                    {hasNewData && (
                      <Badge className="bg-emerald-500 text-white border-0">
                        <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
                        New data
                      </Badge>
                    )}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={handleClearCache}
                    className="flex items-center gap-3"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Clear Cache
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters Section */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500">
                  <Filter className="h-5 w-5 text-white" />
                </div>
                <CardTitle className="text-xl text-foreground">Filter & Search</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ScheduleLogsFilters
                filterType={filterType}
                setFilterType={setFilterType}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedTeam={selectedTeam}
                setSelectedTeam={setSelectedTeam}
                selectedGuard={selectedGuard}
                setSelectedGuard={setSelectedGuard}
                selectedAdmin={selectedAdmin}
                setSelectedAdmin={setSelectedAdmin}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
            </CardContent>
          </Card>

          {/* Results Section */}
          {isLoading ? (
            <Card className="bg-card border-border">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-lg font-medium text-foreground">Loading audit logs...</p>
                    <p className="text-sm text-muted-foreground">Please wait while we fetch the latest data</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {filteredChanges && filteredChanges.length > 0 ? (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <History className="h-5 w-5 text-primary" />
                    <h2 className="text-xl font-semibold text-foreground">
                      {filteredChanges.length} Change{filteredChanges.length !== 1 ? 's' : ''} Found
                    </h2>
                  </div>
                  
                  {filteredChanges.map((change, index) => (
                    <Card 
                      key={change.id} 
                      className={cn(
                        "bg-card border-border hover:shadow-lg transition-all duration-300",
                        "border-l-4 hover:scale-[1.01]"
                      )}
                      style={{
                        borderLeftColor: change.action_type === 'INSERT' ? '#10b981' : 
                                        change.action_type === 'DELETE' ? '#ef4444' : '#3b82f6',
                        animationDelay: `${index * 100}ms`
                      }}
                    >
                      <CardContent className="p-6">
                        {/* Header Row */}
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                          <div className="flex items-center gap-4">
                            <Badge className={cn(
                              "flex items-center gap-2 px-3 py-1 font-medium shadow-sm",
                              getChangeTypeColor(change.action_type)
                            )}>
                              {getChangeTypeIcon(change.action_type)}
                              {getChangeTypeLabel(change.action_type)}
                            </Badge>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="w-4 h-4" />
                              {formatDateTime(change.action_timestamp)}
                            </div>
                          </div>
                          {change.change_reason && (
                            <div className="bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/20">
                              <span className="text-xs font-medium text-blue-600 dark:text-blue-400 italic">
                                {change.change_reason}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Shift Details */}
                        <div className="bg-muted/30 p-6 rounded-xl border border-border mb-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <h4 className="flex items-center gap-2 font-semibold text-foreground">
                                <Calendar className="h-4 w-4 text-primary" />
                                Shift Information
                              </h4>
                              <div className="space-y-3">
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Title</span>
                                  <span className="font-medium text-foreground bg-background px-3 py-2 rounded-lg border border-border">
                                    {change.shift_title || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</span>
                                  <span className="text-muted-foreground bg-background px-3 py-2 rounded-lg border border-border">
                                    {change.shift_description || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Location</span>
                                  <div className="flex items-center gap-2 bg-background px-3 py-2 rounded-lg border border-border">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">{change.shift_location || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-4">
                              <h4 className="flex items-center gap-2 font-semibold text-foreground">
                                <Clock className="h-4 w-4 text-emerald-600" />
                                Schedule Details
                              </h4>
                              <div className="space-y-3">
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Start Time</span>
                                  <span className="font-medium text-foreground bg-background px-3 py-2 rounded-lg border border-border">
                                    {formatDateTime(change.shift_start_date)}
                                  </span>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">End Time</span>
                                  <div className="bg-background px-3 py-2 rounded-lg border border-border">
                                    <span className="font-medium text-foreground">
                                      {formatDateTime(change.shift_end_date)}
                                    </span>
                                    {change.duration_hours && (
                                      <span className="ml-2 text-xs text-muted-foreground">
                                        {formatDuration(change.duration_hours)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Team</span>
                                  <span className="font-medium text-foreground bg-background px-3 py-2 rounded-lg border border-border">
                                    {change.team_name || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Assigned Guards */}
                        {change.assigned_guard_names && change.assigned_guard_names.length > 0 && (
                          <div className="mb-6">
                            <h5 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
                              <User className="h-4 w-4 text-primary" />
                              Assigned Guards:
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {change.assigned_guard_names.map((guardName, guardIndex) => (
                                <Badge key={guardIndex} variant="outline" className="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20">
                                  <User className="w-3 h-3 mr-1" />
                                  {guardName}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Field-specific changes for updates */}
                        {change.action_type === 'UPDATE' && change.field_changed && 
                         formatFieldChange(change.field_changed, change.old_value, change.new_value)}

                        {/* Footer with Action Details */}
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pt-6 border-t border-border">
                          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            {/* Affected Guard */}
                            {change.assigned_guard_names && change.assigned_guard_names.length > 0 && (
                              <div className="flex items-center gap-3 bg-blue-500/10 px-3 py-2 rounded-lg border border-blue-500/20">
                                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                <div className="text-sm">
                                  <span className="text-muted-foreground">Affected:</span>
                                  <span className="ml-2 font-medium text-blue-600 dark:text-blue-400">
                                    {change.assigned_guard_names.join(', ')}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {/* Action By */}
                            <div className="flex items-center gap-3 bg-emerald-500/10 px-3 py-2 rounded-lg border border-emerald-500/20">
                              <User className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                              <div className="text-sm">
                                <span className="text-muted-foreground">Changed by:</span>
                                <span className="ml-2 font-medium text-emerald-600 dark:text-emerald-400">
                                  {change.action_by_name || 'System'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          {change.schedule_id && (
                            <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-1 rounded-full border border-border">
                              ID: {change.schedule_id}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="py-16">
                    <div className="text-center space-y-6">
                      <div className="flex justify-center">
                        <div className="p-6 rounded-2xl bg-muted/30">
                          <Search className="w-16 h-16 text-muted-foreground" />
                        </div>
                      </div>
                      <div className="space-y-3">
                        <CardTitle className="text-2xl text-foreground">No Schedule Changes Found</CardTitle>
                        <CardDescription className="text-lg max-w-md mx-auto">
                          {searchTerm || filterType !== "all" || selectedTeam !== "all" || selectedGuard !== "all" || selectedAdmin !== "all" || selectedDate
                            ? "No changes match your current filters. Try adjusting your search criteria."
                            : "No schedule changes have been recorded yet."
                          }
                        </CardDescription>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default ScheduleLogs;